import { applyPalette, GIFEncoder, quantize } from "gifenc";
import { ArrayBufferTarget, Muxer } from "mp4-muxer";
import { animationDuration, animationFrameId, documentWithAnimationAtTime } from "@/document/animation/applyAnimation";
import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import type { RootAnimationNode } from "@/document/model/animation";
import { opaqueFrameFillClearColor } from "@/export/frameExportBackground";
import { documentForPngExport } from "@/export/pngExporter";
import { exportDocumentToLottie } from "./lottieExporter";
import { exportDocumentAnimationToMermaid } from "./mermaidAnimationExporter";
import type { Rect } from "@/lib/math/rect";
import { Renderer } from "@/renderer/Renderer";
import type { RenderClearColor } from "@/renderer/overlayTypes";
import { prepareWasmMeshKernels } from "@/renderer/scene/tessellation/curvaTessWasm";

export type AnimationExportFormat = "mp4" | "gif" | "webp" | "lottie" | "mermaid";

export interface AnimationExportOptions {
  document: CurvaDocument;
  rootNodeId?: string;
  format: AnimationExportFormat;
  fps?: number;
  scale?: number;
  bitrate?: number;
  onProgress?: (progress: number, frame: number, totalFrames: number) => void;
  signal?: AbortSignal;
}

export interface AnimationExportResult {
  blob: Blob;
  filenameExtension: AnimationExportFormat | "lottie.json" | "mmd";
  mimeType: string;
  durationMs: number;
  frames: number;
  frameName: string;
  codec?: string;
}

type VideoEncoderConstructor = typeof VideoEncoder;
type Mp4VideoCodec = "avc";

interface RenderedAnimationFrame {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

interface Mp4EncoderSelection {
  config: VideoEncoderConfig;
  muxerCodec: Mp4VideoCodec;
  label: string;
}

interface EncodedWebpFrame {
  chunks: Uint8Array[];
}

const DEFAULT_FPS = 30;
const DEFAULT_SCALE = 1;
const DEFAULT_BITRATE = 6_000_000;
/** Safari/WebKit silently fails or misbehaves above ~10 Mbps (mp4-muxer#36). */
const MAX_MP4_BITRATE = 10_000_000;
const YIELD_EVERY_FRAMES = 5;
const H264_LEVELS = [
  { hex: "1E", maxMacroblocks: 1620, maxMacroblocksPerSecond: 40500 },
  { hex: "1F", maxMacroblocks: 3600, maxMacroblocksPerSecond: 108000 },
  { hex: "20", maxMacroblocks: 5120, maxMacroblocksPerSecond: 216000 },
  { hex: "28", maxMacroblocks: 8192, maxMacroblocksPerSecond: 245760 },
  { hex: "2A", maxMacroblocks: 8704, maxMacroblocksPerSecond: 522240 },
  { hex: "32", maxMacroblocks: 22080, maxMacroblocksPerSecond: 589824 },
  { hex: "33", maxMacroblocks: 36864, maxMacroblocksPerSecond: 983040 },
  { hex: "34", maxMacroblocks: 36864, maxMacroblocksPerSecond: 2073600 },
] as const;

export function isMp4AnimationExportSupported(): boolean {
  return typeof VideoEncoder !== "undefined" && typeof VideoFrame !== "undefined";
}

/**
 * True when the runtime can encode WebP via canvas (needed for animated WebP export).
 * Safari/WKWebView can decode WebP but historically cannot encode it — toDataURL falls back to PNG.
 */
export function isWebpAnimationExportSupported(): boolean {
  if (typeof HTMLCanvasElement === "undefined") {
    return false;
  }
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    return false;
  }
}

export function supportedAnimationExportFormats(): AnimationExportFormat[] {
  const formats: AnimationExportFormat[] = ["mp4", "gif", "webp", "lottie", "mermaid"];
  return formats.filter(format => {
    if (format === "mp4") {
      return isMp4AnimationExportSupported();
    }
    if (format === "webp") {
      return isWebpAnimationExportSupported();
    }
    return true;
  });
}

export async function exportAnimation(options: AnimationExportOptions): Promise<AnimationExportResult> {
  if (options.format === "mermaid") {
    const result = exportDocumentAnimationToMermaid({
      document: options.document,
      rootNodeId: options.rootNodeId,
    });
    return {
      blob: new Blob([new TextEncoder().encode(result.mermaid)], { type: "text/vnd.mermaid;charset=utf-8" }),
      filenameExtension: "mmd",
      mimeType: "text/vnd.mermaid;charset=utf-8",
      durationMs: result.durationMs,
      frames: 1,
      frameName: result.frameName,
      codec: "Mermaid",
    };
  }

  if (options.format === "lottie") {
    const result = await exportDocumentToLottie({
      document: options.document,
      rootNodeId: options.rootNodeId,
      fps: options.fps,
      animated: true,
      onProgress: options.onProgress,
      signal: options.signal,
    });
    return {
      blob: new Blob([new TextEncoder().encode(result.json)], { type: "application/json" }),
      filenameExtension: "lottie.json",
      mimeType: "application/json",
      durationMs: result.durationMs,
      frames: result.frames,
      frameName: result.frameName,
      codec: "Lottie JSON",
    };
  }

  const plan = createAnimationExportPlan(options);
  const renderSession = createAnimationRenderSession(options.document, plan.frameBounds, plan.scale, plan.clearColor);
  try {
    if (options.format === "mp4") {
      return await exportAnimationToMp4(options, plan, renderSession);
    }
    if (options.format === "webp") {
      return await exportAnimationToWebp(options, plan, renderSession);
    }
    return await exportAnimationToGif(options, plan, renderSession);
  } finally {
    renderSession.dispose();
  }
}

function createAnimationExportPlan(options: AnimationExportOptions) {
  const graph = options.document.animation;
  const rootNodeId = options.rootNodeId ?? graph?.nodes.find((node): node is RootAnimationNode => node.kind === "animation" && Boolean(node.frameId))?.id;
  const frameId = animationFrameId(graph, rootNodeId);
  if (!frameId) {
    throw new Error("Select an animation node with a frame before exporting");
  }

  const frame = findSceneNode(options.document, frameId);
  if (frame?.type !== "frame") {
    throw new Error("Animation frame not found");
  }

  const durationMs = animationDuration(graph, rootNodeId, options.document);
  if (durationMs <= 0) {
    throw new Error("Animation has no motion to export");
  }

  const fps = Math.max(1, Math.min(120, Math.round(options.fps ?? DEFAULT_FPS)));
  const scale = Math.max(0.1, Math.min(8, options.scale ?? DEFAULT_SCALE));
  const totalFrames = Math.max(1, Math.ceil((durationMs / 1000) * fps));
  const pixelWidth = Math.max(1, Math.ceil(Math.abs(frame.bounds.width) * scale));
  const pixelHeight = Math.max(1, Math.ceil(Math.abs(frame.bounds.height) * scale));

  return {
    rootNodeId,
    frameName: frame.name || "Animation",
    frameBounds: frame.bounds,
    durationMs,
    fps,
    scale,
    clearColor: opaqueFrameFillClearColor(frame),
    bitrate: Math.min(MAX_MP4_BITRATE, Math.max(256_000, options.bitrate ?? DEFAULT_BITRATE * scale * scale)),
    totalFrames,
    pixelWidth,
    pixelHeight,
    videoWidth: evenDimension(pixelWidth),
    videoHeight: evenDimension(pixelHeight),
  };
}

function createAnimationRenderSession(document: CurvaDocument, frameBounds: Rect, scale: number, clearColor?: RenderClearColor) {
  const sourceCanvas = window.document.createElement("canvas");
  sourceCanvas.style.cssText = "position:fixed;left:-10000px;top:0;pointer-events:none;";
  window.document.body.appendChild(sourceCanvas);
  const renderer = new Renderer(sourceCanvas, { alpha: false, preserveDrawingBuffer: true });
  const meshReady = prepareWasmMeshKernels();

  const compositeCanvas = window.document.createElement("canvas");
  const compositeContext = requiredContext2d(compositeCanvas);

  return {
    async render(timeMs: number, rootNodeId: string | undefined, width: number, height: number): Promise<RenderedAnimationFrame> {
      await meshReady;
      const evaluation = documentWithAnimationAtTime(document, timeMs, rootNodeId);
      const animatedDocument = evaluation.document;
      const animatedFrame = findSceneNode(animatedDocument, evaluation.displayFrameId ?? animationFrameId(animatedDocument.animation, rootNodeId) ?? "");
      if (animatedFrame?.type !== "frame") {
        throw new Error("Animation frame not found");
      }

      const exportDocument = documentForPngExport(animatedDocument, animatedFrame);
      await renderer.renderDocumentToCanvas(exportDocument, frameBounds, scale, { clearColor });

      compositeCanvas.width = width;
      compositeCanvas.height = height;
      compositeContext.fillStyle = "#ffffff";
      compositeContext.fillRect(0, 0, width, height);
      compositeContext.drawImage(sourceCanvas, 0, 0);
      return { canvas: compositeCanvas, width, height };
    },
    dispose() {
      renderer.dispose();
      sourceCanvas.remove();
    },
  };
}

async function exportAnimationToMp4(
  options: AnimationExportOptions,
  plan: ReturnType<typeof createAnimationExportPlan>,
  renderSession: ReturnType<typeof createAnimationRenderSession>,
): Promise<AnimationExportResult> {
  if (!isMp4AnimationExportSupported()) {
    throw new Error("MP4 export needs WebCodecs (VideoEncoder). Use Chrome, Safari 17+, or the Curva desktop app.");
  }

  const encoderConstructor = VideoEncoder as VideoEncoderConstructor;
  const selection = await selectMp4EncoderConfig(encoderConstructor, plan);

  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    video: {
      codec: selection.muxerCodec,
      width: plan.videoWidth,
      height: plan.videoHeight,
      frameRate: plan.fps,
    },
    fastStart: "in-memory",
  });
  let encoderError: Error | undefined;
  // Safari/WKWebView often omits EncodedVideoChunk.duration; mp4-muxer requires a finite ≥0 value.
  const frameDurationUs = Math.max(1, Math.round(1_000_000 / plan.fps));
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      try {
        appendEncodedVideoChunk(muxer, chunk, meta, frameDurationUs);
      } catch (error) {
        encoderError = error instanceof Error ? error : new Error(String(error));
      }
    },
    error: error => {
      encoderError = error instanceof Error ? error : new Error(String(error));
    },
  });

  encoder.configure(selection.config);
  try {
    for (let frame = 0; frame < plan.totalFrames; frame += 1) {
      throwIfAborted(options.signal);
      const rendered = await renderSession.render(frameTimeMs(frame, plan.fps, plan.durationMs), plan.rootNodeId, plan.videoWidth, plan.videoHeight);
      const videoFrame = new VideoFrame(rendered.canvas, {
        timestamp: Math.round(frame * frameDurationUs),
        duration: frameDurationUs,
      });
      encoder.encode(videoFrame, { keyFrame: frame % plan.fps === 0 });
      videoFrame.close();
      options.onProgress?.((frame + 1) / plan.totalFrames, frame + 1, plan.totalFrames);
      if (encoderError) throw encoderError;
      if (frame % YIELD_EVERY_FRAMES === 0) {
        await yieldToMainThread();
      }
    }

    await encoder.flush();
    if (encoderError) throw encoderError;
    muxer.finalize();
    return {
      blob: new Blob([muxer.target.buffer], { type: "video/mp4" }),
      filenameExtension: "mp4",
      mimeType: "video/mp4",
      durationMs: plan.durationMs,
      frames: plan.totalFrames,
      frameName: plan.frameName,
      codec: selection.label,
    };
  } finally {
    if (encoder.state !== "closed") {
      encoder.close();
    }
  }
}

function appendEncodedVideoChunk(
  muxer: Muxer<ArrayBufferTarget>,
  chunk: EncodedVideoChunk,
  meta: EncodedVideoChunkMetadata | undefined,
  fallbackDurationUs: number,
): void {
  const data = new Uint8Array(chunk.byteLength);
  chunk.copyTo(data);
  const duration = Number.isFinite(chunk.duration) && (chunk.duration as number) >= 0
    ? (chunk.duration as number)
    : fallbackDurationUs;
  muxer.addVideoChunkRaw(data, chunk.type, chunk.timestamp, duration, sanitizeEncodedVideoChunkMetadata(meta));
}

/**
 * Safari/WebKit may omit decoderConfig.colorSpace or leave primaries/transfer/matrix null.
 * mp4-muxer's colr() box assumes a full VideoColorSpace when the object is truthy.
 */
function sanitizeEncodedVideoChunkMetadata(
  meta: EncodedVideoChunkMetadata | undefined,
): EncodedVideoChunkMetadata | undefined {
  if (!meta?.decoderConfig) {
    return meta;
  }
  const colorSpace = meta.decoderConfig.colorSpace;
  if (
    colorSpace == null
    || colorSpace.primaries == null
    || colorSpace.transfer == null
    || colorSpace.matrix == null
  ) {
    const { colorSpace: _omitted, ...decoderConfig } = meta.decoderConfig;
    return { ...meta, decoderConfig };
  }
  return meta;
}

async function selectMp4EncoderConfig(
  encoderConstructor: VideoEncoderConstructor,
  plan: ReturnType<typeof createAnimationExportPlan>,
): Promise<Mp4EncoderSelection> {
  const base = {
    width: plan.videoWidth,
    height: plan.videoHeight,
    bitrate: plan.bitrate,
    framerate: plan.fps,
    latencyMode: "quality" as const,
    alpha: "discard" as const,
  };
  const h264Level = h264LevelForVideo(plan.videoWidth, plan.videoHeight, plan.fps);
  const candidates: Mp4EncoderSelection[] = [
    { codec: `avc1.6400${h264Level}`, profile: "High" },
    { codec: `avc1.4D40${h264Level}`, profile: "Main" },
    { codec: `avc1.42E0${h264Level}`, profile: "Constrained Baseline" },
    { codec: `avc1.4200${h264Level}`, profile: "Baseline" },
  ].map(({ codec, profile }) => ({
    config: { ...base, codec, avc: { format: "avc" as const } },
    muxerCodec: "avc" as const,
    label: `H.264 ${profile} ${codec}`,
  }));

  if (typeof encoderConstructor.isConfigSupported !== "function") {
    return candidates[0]!;
  }

  for (const candidate of candidates) {
    try {
      const supported = await encoderConstructor.isConfigSupported(candidate.config);
      if (supported.supported) {
        return { ...candidate, config: supported.config ?? candidate.config };
      }
    } catch {
      // Keep probing; Chromium rejects some codec strings by throwing instead of returning supported:false.
    }
  }

  throw new Error("This runtime cannot encode a QuickTime-compatible H.264 MP4. Try exporting from Chrome or an Electron build with H.264 WebCodecs support.");
}

function h264LevelForVideo(width: number, height: number, fps: number): string {
  const macroblocks = Math.ceil(width / 16) * Math.ceil(height / 16);
  const macroblocksPerSecond = macroblocks * fps;
  return (
    H264_LEVELS.find(level => macroblocks <= level.maxMacroblocks && macroblocksPerSecond <= level.maxMacroblocksPerSecond) ??
    H264_LEVELS[H264_LEVELS.length - 1]!
  ).hex;
}

async function exportAnimationToGif(
  options: AnimationExportOptions,
  plan: ReturnType<typeof createAnimationExportPlan>,
  renderSession: ReturnType<typeof createAnimationRenderSession>,
): Promise<AnimationExportResult> {
  const gif = GIFEncoder();
  const delay = Math.max(1, Math.round(1000 / plan.fps));

  for (let frame = 0; frame < plan.totalFrames; frame += 1) {
    throwIfAborted(options.signal);
    const rendered = await renderSession.render(frameTimeMs(frame, plan.fps, plan.durationMs), plan.rootNodeId, plan.pixelWidth, plan.pixelHeight);
    const imageData = requiredContext2d(rendered.canvas).getImageData(0, 0, rendered.width, rendered.height);
    const palette = quantize(imageData.data, 256, { format: "rgb565" });
    const index = applyPalette(imageData.data, palette, "rgb565");
    gif.writeFrame(index, rendered.width, rendered.height, { palette, delay, repeat: 0 });
    options.onProgress?.((frame + 1) / plan.totalFrames, frame + 1, plan.totalFrames);
    if (frame % YIELD_EVERY_FRAMES === 0) {
      await yieldToMainThread();
    }
  }

  gif.finish();
  return {
    blob: new Blob([arrayBufferForBlob(gif.bytes())], { type: "image/gif" }),
    filenameExtension: "gif",
    mimeType: "image/gif",
    durationMs: plan.durationMs,
    frames: plan.totalFrames,
    frameName: plan.frameName,
  };
}

async function exportAnimationToWebp(
  options: AnimationExportOptions,
  plan: ReturnType<typeof createAnimationExportPlan>,
  renderSession: ReturnType<typeof createAnimationRenderSession>,
): Promise<AnimationExportResult> {
  if (!isWebpAnimationExportSupported()) {
    throw new Error("This browser cannot encode WebP. Export GIF or MP4 instead, or use Chrome.");
  }

  const frames: EncodedWebpFrame[] = [];
  const delayMs = Math.max(1, Math.round(1000 / plan.fps));

  for (let frame = 0; frame < plan.totalFrames; frame += 1) {
    throwIfAborted(options.signal);
    const rendered = await renderSession.render(frameTimeMs(frame, plan.fps, plan.durationMs), plan.rootNodeId, plan.pixelWidth, plan.pixelHeight);
    const blob = await canvasToBlob(rendered.canvas, "image/webp", 0.92);
    frames.push({ chunks: extractWebpFrameChunks(new Uint8Array(await blob.arrayBuffer())) });
    options.onProgress?.((frame + 1) / plan.totalFrames, frame + 1, plan.totalFrames);
    if (frame % YIELD_EVERY_FRAMES === 0) {
      await yieldToMainThread();
    }
  }

  return {
    blob: new Blob([arrayBufferForBlob(muxAnimatedWebp(frames, plan.pixelWidth, plan.pixelHeight, delayMs))], { type: "image/webp" }),
    filenameExtension: "webp",
    mimeType: "image/webp",
    durationMs: plan.durationMs,
    frames: plan.totalFrames,
    frameName: plan.frameName,
    codec: "Animated WebP",
  };
}

function requiredContext2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new Error("Could not create export canvas context");
  }
  return context;
}

function evenDimension(value: number): number {
  return value % 2 === 0 ? value : value + 1;
}

function frameTimeMs(frame: number, fps: number, durationMs: number): number {
  return Math.min(durationMs, (frame / fps) * 1000);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new Error("Animation export cancelled");
  }
}

async function yieldToMainThread(): Promise<void> {
  await new Promise(resolve => window.setTimeout(resolve, 0));
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error(`Could not encode ${mimeType}`));
      }
    }, mimeType, quality);
  });
}

function extractWebpFrameChunks(bytes: Uint8Array): Uint8Array[] {
  if (readAscii(bytes, 0, 4) !== "RIFF" || readAscii(bytes, 8, 4) !== "WEBP") {
    throw new Error("Browser returned an invalid WebP frame");
  }

  const chunks: Uint8Array[] = [];
  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const type = readAscii(bytes, offset, 4);
    const size = readUint32(bytes, offset + 4);
    const paddedSize = size + (size % 2);
    if (type === "VP8 " || type === "VP8L" || type === "ALPH") {
      chunks.push(bytes.slice(offset, offset + 8 + paddedSize));
    }
    offset += 8 + paddedSize;
  }

  if (chunks.length === 0) {
    throw new Error("Browser WebP encoder did not produce frame image chunks");
  }
  return chunks;
}

function muxAnimatedWebp(frames: EncodedWebpFrame[], width: number, height: number, delayMs: number): Uint8Array {
  const chunks: Uint8Array[] = [
    createChunk("VP8X", concatBytes([
      new Uint8Array([0x02, 0, 0, 0]),
      uint24(width - 1),
      uint24(height - 1),
    ])),
    createChunk("ANIM", new Uint8Array([255, 255, 255, 255, 0, 0])),
  ];

  for (const frame of frames) {
    chunks.push(createChunk("ANMF", concatBytes([
      uint24(0),
      uint24(0),
      uint24(width - 1),
      uint24(height - 1),
      uint24(delayMs),
      new Uint8Array([0]),
      ...frame.chunks,
    ])));
  }

  const body = concatBytes([ascii("WEBP"), ...chunks]);
  return concatBytes([ascii("RIFF"), uint32(body.length), body]);
}

function createChunk(type: string, payload: Uint8Array): Uint8Array {
  const padding = payload.length % 2 === 1 ? 1 : 0;
  return concatBytes([
    ascii(type),
    uint32(payload.length),
    payload,
    padding ? new Uint8Array([0]) : new Uint8Array(),
  ]);
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

function arrayBufferForBlob(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function ascii(value: string): Uint8Array {
  const output = new Uint8Array(value.length);
  for (let index = 0; index < value.length; index += 1) {
    output[index] = value.charCodeAt(index);
  }
  return output;
}

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  let result = "";
  for (let index = 0; index < length; index += 1) {
    result += String.fromCharCode(bytes[offset + index] ?? 0);
  }
  return result;
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) |
    ((bytes[offset + 1] ?? 0) << 8) |
    ((bytes[offset + 2] ?? 0) << 16) |
    ((bytes[offset + 3] ?? 0) << 24);
}

function uint32(value: number): Uint8Array {
  return new Uint8Array([
    value & 0xff,
    (value >> 8) & 0xff,
    (value >> 16) & 0xff,
    (value >> 24) & 0xff,
  ]);
}

function uint24(value: number): Uint8Array {
  return new Uint8Array([
    value & 0xff,
    (value >> 8) & 0xff,
    (value >> 16) & 0xff,
  ]);
}
